import React, { useContext, useEffect, useState } from "react";
import PropTypes from "prop-types";
import { PAGE_STATE } from "../../constants";
import useUsers from "../../hooks/useUsers";
import useCommissions from "../../hooks/useCommissions";
import List, { STATE } from "../List";
import SecondaryPagination from "../SecondaryPagination/SecondaryPagination";
import AppContext from "../../AppContext";

import "../../styles/commission.scss";
import _ from "lodash";
import { format } from "date-fns";

const TABS = {
  HIRED: 1,
  WORKS: 2,
};

const tabs = [
  {
    name: "Works",
    value: TABS.WORKS,
  },
  {
    name: "My Commissions",
    value: TABS.HIRED,
  },
];

const CommissionUser = ({ avatar, firstname, surname }) => {
  return (
    <div className="commissions-content-list-user">
      <img
        className="commissions-content-list-user__avatar"
        alt="profile-avatar"
        src={avatar}
      />
      <span className="commissions-content-list-user__name">{`${firstname} ${
        surname || ""
      }`}</span>
    </div>
  );
};

const CommissionStatus = ({ status }) => {
  const statusNames = {
    0: "Quotation",
    1: "Waiting for payment",
    2: "In progress",
    3: "Waiting confirmation",
    4: "Completed",
    5: "Cancelled",
  };

  return (
    <span
      className={`commissions-content-list-status commissions-content-list-status--${status}`}
    >
      {statusNames[status]}
    </span>
  );
};

const Commission = (props) => {
  const { user } = useContext(AppContext);
  const { queryUsers } = useUsers();
  const { queryCommissions } = useCommissions();
  const [filter, setFilter] = useState({});
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [metadata, setMetadata] = useState({
    currentPage: 1,
    totalPage: 1,
  });
  const [dataSource, setDataSource] = useState(undefined);
  const [dataState, setDataState] = useState(STATE.LOADING);
  const [defaultFilter, setDefaultFilter] = useState(undefined);
  const [selectedTab, setSelectedTab] = useState(TABS.WORKS);
  const [columns, setColumns] = useState(undefined);

  useEffect(() => {
    if (selectedTab === TABS.HIRED) {
      setColumns([
        {
          display: "Project Name",
          key: "name",
          textAlign: "left",
        },
        {
          display: "Commissioned To",
          key: "commissionedTo",
          textAlign: "left",
          maxWidth: 150,
        },
        {
          display: "Price",
          key: "price",
          textAlign: "left",
          maxWidth: 100,
        },
        {
          display: "Commissioned on",
          key: "createdOn",
          textAlign: "left",
          maxWidth: 80,
        },
        {
          display: "Status",
          key: "status",
          textAlign: "left",
          maxWidth: 100,
        },
        {
          display: "",
          key: "actions",
          textAlign: "left",
          maxWidth: 50,
        },
      ]);
      setDefaultFilter({ commissionedBy: user._id });
      return;
    }

    setColumns([
      {
        display: "Project Name",
        key: "name",
        textAlign: "left",
      },
      {
        display: "Hired By",
        key: "hiredBy",
        textAlign: "left",
        maxWidth: 150,
      },
      {
        display: "Price",
        key: "price",
        textAlign: "left",
        maxWidth: 100,
      },
      {
        display: "Commissioned on",
        key: "createdOn",
        textAlign: "left",
        maxWidth: 120,
      },
      {
        display: "Status",
        key: "status",
        textAlign: "left",
        maxWidth: 100,
      },
      {
        display: "",
        key: "actions",
        textAlign: "left",
        maxWidth: 50,
      },
    ]);
    setDefaultFilter({ createdBy: user._id });
  }, [selectedTab]);

  useEffect(() => {
    if (!defaultFilter) return;

    fetchData(filter, page, limit);
  }, [defaultFilter, filter, page, limit]);

  const fetchData = async (filter, page, limit) => {
    const { data: _commissions, _metadata } = await queryCommissions(
      { ...filter, ...defaultFilter, active: true, deleted: false },
      page,
      limit
    );

    let userIds = [];

    for (const commission of _.cloneDeep(_commissions)) {
      userIds.push(commission.createdBy);
      userIds.push(commission.commissionedBy);
    }

    const { data: _users } = await queryUsers(
      {
        _id: {
          $in: userIds,
        },
      },
      1,
      userIds.length,
      {
        firstname: 1,
        surname: 1,
        avatar: 1,
      }
    );

    setDataSource(
      _commissions.map((commission) => {
        const { name, price, commissionedBy, createdBy, createdOn, status } =
          commission;

        let baseRecord = {
          name,
          price: `${(+price).toLocaleString()} THB`,
          commissionedBy,
          createdOn: format(new Date(createdOn), "dd/MM/yyyy HH:mm"),
          status: <CommissionStatus status={status} />,
        };

        if (selectedTab === TABS.WORKS) {
          const userMatch = _users.find((u) => u._id === commissionedBy);

          baseRecord.hiredBy = (
            <CommissionUser
              avatar={userMatch.avatar}
              firstname={userMatch.firstname}
              surname={userMatch.surname}
            />
          );
        }

        if (selectedTab === TABS.HIRED) {
          const userMatch = _users.find((u) => u._id === createdBy);

          baseRecord.commissionedTo = (
            <CommissionUser
              avatar={userMatch.avatar}
              firstname={userMatch.firstname}
              surname={userMatch.surname}
            />
          );
        }

        return baseRecord;
      })
    );
    setMetadata(_metadata);
    setDataState(STATE.IDLE);
  };

  const handleOnPageChange = (e) => setPage(e);

  const handleOnLimitChange = (e) => setLimit(e);

  return (
    <div className="commissions">
      <h1 className="commissions__heading">Commissions</h1>
      <div className="commissions-content">
        <div className="commissions-content-header">
          <div className="commissions-content-header-tabs">
            {tabs.map(({ name, value }) => {
              return (
                <span
                  className={`commissions-content-header-tabs-item ${
                    value === selectedTab
                      ? "commissions-content-header-tabs-item--active"
                      : ""
                  }`}
                  key={value}
                  onClick={() => setSelectedTab(value)}
                >
                  {name}
                </span>
              );
            })}
          </div>
        </div>
        <List
          className="commissions-content-list"
          contentState={dataState}
          columns={columns || []}
          dataSource={dataSource}
          onRecordClick={(e) => handleSelected(e)}
        >
          <SecondaryPagination
            limits={[10, 50]}
            defaultPage={metadata?.currentPage || 1}
            pages={metadata?.totalPages || 1}
            onPageChange={handleOnPageChange}
            onLimitChange={handleOnLimitChange}
          />
        </List>
      </div>
    </div>
  );
};

Commission.propTypes = {};

export default Commission;
